import React, { useEffect, useState, useRef } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Tabs } from '../ui/Tabs';
import { EmailNotificationsConfig } from './EmailNotificationsConfig';
import { UserManagement } from './UserManagement';
import { Roles } from './Roles';
import { Permissions } from './Permissions';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { hasPermission } from '../../types';
import { UserIcon, SettingsIcon, BellIcon, ShieldIcon, MailIcon, UsersIcon, KeyIcon, ShieldCheckIcon, CameraIcon, EyeIcon } from 'lucide-react';
interface NotificationSettings {
  newAppointments: boolean;
  cancelledAppointments: boolean;
  newPatients: boolean;
}
interface Preferences {
  darkMode: boolean;
  desktopNotifications: boolean;
}
function ReadOnlyBanner() {
  return <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 flex items-center gap-2 text-sm" data-id="element-2073">
      <EyeIcon className="w-4 h-4 flex-shrink-0" data-id="element-2074" />
      <span data-id="element-2075">Solo lectura — No tienes permisos para editar esta sección.</span>
    </div>;
}
export function Settings() {
  const {
    user,
    updateUser
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const userRole = user?.role || '';
  // Permission checks for editing (need 'full' or 'own' to edit, not just 'read')
  const canEditProfile = hasPermission(userRole, 'settings_profile', 'full');
  const canEditPreferences = hasPermission(userRole, 'settings_preferences', 'full');
  const canEditNotifications = hasPermission(userRole, 'settings_notifications', 'full');
  const canEditSecurity = hasPermission(userRole, 'settings_security', 'full');
  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaved, setProfileSaved] = useState(false);
  const [avatarSaved, setAvatarSaved] = useState(false);
  const [pendingAvatar, setPendingAvatar] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Security state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  // Preferences state
  const [preferences, setPreferences] = useState<Preferences>({
    darkMode: theme === 'dark',
    desktopNotifications: false
  });
  // Notification settings state
  const [notifications, setNotifications] = useState<NotificationSettings>({
    newAppointments: true,
    cancelledAppointments: true,
    newPatients: true
  });
  // Load preferences from localStorage
  useEffect(() => {
    const savedPrefs = localStorage.getItem('savidna-preferences');
    if (savedPrefs) {
      try {
        const parsed = JSON.parse(savedPrefs);
        setPreferences(parsed);
      } catch (e) {
        console.error('Error loading preferences:', e);
      }
    }
    const savedNotifs = localStorage.getItem('savidna-notification-settings');
    if (savedNotifs) {
      try {
        const parsed = JSON.parse(savedNotifs);
        setNotifications(parsed);
      } catch (e) {
        console.error('Error loading notification settings:', e);
      }
    }
  }, []);
  // Save preferences to localStorage
  const savePreferences = (newPrefs: Preferences) => {
    setPreferences(newPrefs);
    localStorage.setItem('savidna-preferences', JSON.stringify(newPrefs));
  };
  // Save notification settings to localStorage
  const saveNotifications = (newNotifs: NotificationSettings) => {
    setNotifications(newNotifs);
    localStorage.setItem('savidna-notification-settings', JSON.stringify(newNotifs));
  };
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen es demasiado grande. El tamaño máximo es 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setPendingAvatar(base64String);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSaveAvatar = () => {
    if (!pendingAvatar) return;
    if (updateUser) {
      updateUser({
        avatar: pendingAvatar
      });
    }
    // Also persist to localStorage users list
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          avatar: pendingAvatar
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
      } catch (e) {
        console.error('Error saving avatar:', e);
      }
    }
    setPendingAvatar(null);
    setAvatarSaved(true);
    setTimeout(() => setAvatarSaved(false), 3000);
  };
  const handleSaveProfile = () => {
    if (!canEditProfile) return;
    if (!name || !email) {
      alert('Por favor completa todos los campos');
      return;
    }
    if (updateUser) {
      updateUser({
        name,
        email
      });
    }
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          name,
          email
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
      } catch (e) {
        console.error('Error updating user:', e);
      }
    }
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 3000);
  };
  const handleChangePassword = () => {
    if (!canEditSecurity) return;
    if (!currentPassword || !newPassword || !confirmPassword) {
      alert('Por favor completa todos los campos');
      return;
    }
    if (newPassword !== confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const currentUser = users.find((u: any) => u.id === user?.id);
        if (currentUser && currentUser.password !== currentPassword) {
          alert('La contraseña actual es incorrecta');
          return;
        }
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          password: newPassword
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordSaved(true);
        setTimeout(() => setPasswordSaved(false), 3000);
      } catch (e) {
        console.error('Error updating password:', e);
        alert('Error al cambiar la contraseña');
      }
    }
  };
  const handleToggleDarkMode = (checked: boolean) => {
    if (!canEditPreferences) return;
    savePreferences({
      ...preferences,
      darkMode: checked
    });
    toggleTheme();
  };
  const handleToggleDesktopNotifications = (checked: boolean) => {
    if (!canEditPreferences) return;
    savePreferences({
      ...preferences,
      desktopNotifications: checked
    });
    if (checked && 'Notification' in window) {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('Notificaciones activadas', {
            body: 'Ahora recibirás notificaciones de Savidna',
            icon: 'https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg'
          });
        }
      });
    }
  };
  // All possible tabs with their permission keys
  const allTabs = [{
    id: 'profile',
    label: 'Perfil',
    icon: <UserIcon className="w-4 h-4" data-id="element-2076" />,
    permissionKey: 'settings_profile' as const,
    content: <div className="space-y-6" data-id="element-2077">
          {!canEditProfile && <ReadOnlyBanner data-id="element-2078" />}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-2079">
            <div className="h-32 bg-gradient-to-r from-sky-400 to-blue-500 dark:from-sky-600 dark:to-blue-700" data-id="element-2080"></div>
            <div className="px-8 pb-8" data-id="element-2081">
              <div className="relative flex justify-between items-end -mt-12 mb-6" data-id="element-2082">
                <div className="relative group" data-id="element-2083">
                  <div className="w-24 h-24 rounded-full border-4 border-white dark:border-gray-800 bg-white dark:bg-gray-700 overflow-hidden relative shadow-md cursor-pointer" onClick={handleAvatarClick} data-id="element-2084">
                    {pendingAvatar || user?.avatar ? <img src={pendingAvatar || user?.avatar} alt={user?.name || ''} className="w-full h-full object-cover" data-id="element-2085" /> : <div className="w-full h-full flex items-center justify-center bg-sky-100 dark:bg-sky-900 text-sky-600 dark:text-sky-400 text-3xl font-bold" data-id="element-2086">
                        {name.charAt(0).toUpperCase()}
                      </div>}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-2087">
                      <CameraIcon className="w-8 h-8 text-white" data-id="element-2088" />
                    </div>
                  </div>
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} data-id="element-2089" />
                </div>
                <div className="flex-1 ml-6 mb-2" data-id="element-2090">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2091">
                    {name}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-2092">{email}</p>
                  {pendingAvatar && <Button onClick={handleSaveAvatar} className="mt-2" size="sm" data-id="element-2093">
                      <CameraIcon className="w-4 h-4 mr-1" data-id="element-2094" />
                      Guardar Foto
                    </Button>}
                </div>
                <div data-id="element-2095">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300 border border-sky-200 dark:border-sky-800 capitalize" data-id="element-2096">
                    {user?.role || 'Usuario'}
                  </span>
                </div>
              </div>
              {avatarSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 flex items-center gap-2" data-id="element-2097">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-2098" />
                  <span className="font-medium" data-id="element-2099">
                    Foto de perfil actualizada correctamente
                  </span>
                </div>}
              {profileSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 flex items-center gap-2" data-id="element-2100">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-2101" />
                  <span className="font-medium" data-id="element-2102">
                    Perfil actualizado correctamente
                  </span>
                </div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2103">
                <div className="space-y-4" data-id="element-2104">
                  <Input label="Nombre Completo" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" disabled={!canEditProfile} data-id="element-2105" />
                  <Input label="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" disabled={!canEditProfile} data-id="element-2106" />
                </div>
                <div className="space-y-4" data-id="element-2107">
                  <Input label="Cargo / Rol" value={user?.role || 'Administrador'} disabled className="bg-gray-50 dark:bg-gray-900/50" data-id="element-2108" />
                  {canEditProfile && <div className="pt-7" data-id="element-2109">
                      <Button onClick={handleSaveProfile} className="w-full" data-id="element-2110">
                        Guardar Cambios
                      </Button>
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: 'preferences',
    label: 'Preferencias',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-2111" />,
    permissionKey: 'settings_preferences' as const,
    content: <Card data-id="element-2112">
          <div className="space-y-6" data-id="element-2113">
            {!canEditPreferences && <ReadOnlyBanner data-id="element-2114" />}
            <div data-id="element-2115">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2116">
                Preferencias del Sistema
              </h3>
              <div className="space-y-4" data-id="element-2117">
                <div className="flex items-center justify-between" data-id="element-2118">
                  <div data-id="element-2119">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-2120">
                      Modo Oscuro
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2121">
                      Activar tema oscuro automáticamente
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditPreferences ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-2122">
                    <input type="checkbox" checked={preferences.darkMode} onChange={e => handleToggleDarkMode(e.target.checked)} className="sr-only peer" disabled={!canEditPreferences} data-id="element-2123" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-2124"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between" data-id="element-2125">
                  <div data-id="element-2126">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-2127">
                      Notificaciones de Escritorio
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2128">
                      Recibir notificaciones en el navegador
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditPreferences ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-2129">
                    <input type="checkbox" checked={preferences.desktopNotifications} onChange={e => handleToggleDesktopNotifications(e.target.checked)} className="sr-only peer" disabled={!canEditPreferences} data-id="element-2130" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-2131"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'notifications',
    label: 'Notificaciones',
    icon: <BellIcon className="w-4 h-4" data-id="element-2132" />,
    permissionKey: 'settings_notifications' as const,
    content: <Card data-id="element-2133">
          <div className="space-y-6" data-id="element-2134">
            {!canEditNotifications && <ReadOnlyBanner data-id="element-2135" />}
            <div data-id="element-2136">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2137">
                Configuración de Notificaciones
              </h3>
              <div className="space-y-4" data-id="element-2138">
                <div className="flex items-center justify-between" data-id="element-2139">
                  <div data-id="element-2140">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-2141">
                      Nuevas Citas
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2142">
                      Notificar cuando se crea una nueva cita
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditNotifications ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-2143">
                    <input type="checkbox" checked={notifications.newAppointments} onChange={e => canEditNotifications && saveNotifications({
                  ...notifications,
                  newAppointments: e.target.checked
                })} className="sr-only peer" disabled={!canEditNotifications} data-id="element-2144" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-2145"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between" data-id="element-2146">
                  <div data-id="element-2147">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-2148">
                      Citas Canceladas
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2149">
                      Notificar cuando se cancela una cita
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditNotifications ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-2150">
                    <input type="checkbox" checked={notifications.cancelledAppointments} onChange={e => canEditNotifications && saveNotifications({
                  ...notifications,
                  cancelledAppointments: e.target.checked
                })} className="sr-only peer" disabled={!canEditNotifications} data-id="element-2151" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-2152"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between" data-id="element-2153">
                  <div data-id="element-2154">
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-2155">
                      Nuevos Pacientes
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2156">
                      Notificar cuando se registra un nuevo paciente
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditNotifications ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-2157">
                    <input type="checkbox" checked={notifications.newPatients} onChange={e => canEditNotifications && saveNotifications({
                  ...notifications,
                  newPatients: e.target.checked
                })} className="sr-only peer" disabled={!canEditNotifications} data-id="element-2158" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 dark:peer-focus:ring-sky-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sky-600" data-id="element-2159"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'security',
    label: 'Seguridad',
    icon: <ShieldIcon className="w-4 h-4" data-id="element-2160" />,
    permissionKey: 'settings_security' as const,
    content: <Card data-id="element-2161">
          <div className="space-y-6" data-id="element-2162">
            {!canEditSecurity && <ReadOnlyBanner data-id="element-2163" />}
            <div data-id="element-2164">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2165">
                Seguridad de la Cuenta
              </h3>
              {passwordSaved && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm" data-id="element-2166">
                  ✓ Contraseña cambiada correctamente
                </div>}
              {canEditSecurity ? <div className="space-y-4" data-id="element-2167">
                  <Input label="Contraseña Actual" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-2168" />
                  <Input label="Nueva Contraseña" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-2169" />
                  <Input label="Confirmar Nueva Contraseña" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-2170" />
                  <Button onClick={handleChangePassword} data-id="element-2171">
                    Cambiar Contraseña
                  </Button>
                </div> : <div className="space-y-4" data-id="element-2172">
                  <Input label="Contraseña Actual" type="password" value="••••••••" disabled placeholder="••••••••" data-id="element-2173" />
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2174">
                    No tienes permisos para cambiar la contraseña. Contacta a un
                    administrador.
                  </p>
                </div>}
            </div>
          </div>
        </Card>
  }, {
    id: 'email',
    label: 'Correos',
    icon: <MailIcon className="w-4 h-4" data-id="element-2175" />,
    permissionKey: 'settings_email' as const,
    content: <EmailNotificationsConfig data-id="element-2176" />
  }, {
    id: 'users',
    label: 'Usuarios',
    icon: <UsersIcon className="w-4 h-4" data-id="element-2177" />,
    permissionKey: 'settings_users' as const,
    content: <UserManagement data-id="element-2178" />
  }, {
    id: 'roles-permissions',
    label: 'Roles y Permisos',
    icon: <KeyIcon className="w-4 h-4" data-id="element-2179" />,
    permissionKey: 'settings_roles_permissions' as const,
    content: <div className="space-y-6" data-id="element-2180">
          <Roles data-id="element-2181" />
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6" data-id="element-2182">
            <Permissions data-id="element-2183" />
          </div>
        </div>
  }];
  // Filter tabs based on user permissions
  const tabs = allTabs.filter(tab => hasPermission(userRole, tab.permissionKey, 'read'));
  // Find the first available tab for default
  const defaultTab = tabs.length > 0 ? tabs[0].id : 'profile';
  return <div className="space-y-6" data-id="element-2184">
      <div data-id="element-2185">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2186">
          Configuración
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1" data-id="element-2187">
          Administra tu cuenta y preferencias
        </p>
      </div>

      {tabs.length > 0 ? <Tabs tabs={tabs} defaultTab={defaultTab} data-id="element-2188" /> : <Card data-id="element-2189">
          <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-2190">
            <SettingsIcon className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" data-id="element-2191" />
            <p data-id="element-2192">No tienes acceso a ninguna sección de configuración</p>
          </div>
        </Card>}
    </div>;
}